import { useCallback, useEffect, useRef, useState } from 'react';
import { TourOverlay } from './components/TourOverlay';
import { type TourPlacement } from '../../constants/FeatureTourConstants';

export { TOUR_PLACEMENT } from '../../constants/FeatureTourConstants';

export type { TourPlacement };

export type PopoverOffset = {
  top?: number;
  left?: number;
};

export type RectOffset = {
  top?: number;
  left?: number;
  width?: number;
  height?: number;
};

export type Rect = { top: number; left: number; width: number; height: number };

export type FeatureTourStep = {
  // human readable id
  id: string;
  // css selector
  target: string;
  title?: string;
  body: React.ReactNode;
  placement?: TourPlacement;
  // fine-tune popover position
  popoverOffset?: PopoverOffset;
  // fine-tune the spotlight/target rect used for highlighting and popover placement
  targetRectOffset?: RectOffset;
};

type Props = {
  tourId: string;
  steps: Array<FeatureTourStep>;
  // default padding for all steps (default: 8)
  spotlightPadding?: number;
  // allow scrolling while tour is open (default: false)
  allowScroll?: boolean;
  onComplete?: () => void;
};

const safeQuerySelector = (selector: string): HTMLElement | null => {
  try {
    return document.querySelector(selector) as HTMLElement | null;
  } catch {
    return null;
  }
};

const MAX_ATTEMPTS = 20;
const POLL_INTERVAL = 100;
const DEFAULT_SPOTLIGHT_PADDING = 8;

export function FeatureTour({
  steps,
  spotlightPadding = DEFAULT_SPOTLIGHT_PADDING,
  allowScroll = false,
  onComplete,
}: Props) {
  const [open, setOpen] = useState(true);
  const [index, setIndex] = useState(0);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);

  const step = steps[index];

  const close = useCallback(() => setOpen(false), []);

  const complete = useCallback(() => {
    setOpen(false);
    onComplete?.();
  }, [onComplete]);

  // Ref so the polling effect always calls the latest complete without re-registering
  const completeRef = useRef(complete);
  useEffect(() => {
    completeRef.current = complete;
  }, [complete]);

  useEffect(() => {
    if (!open) {
      setTargetEl(null);
      return;
    }

    if (!step) return;

    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    // Keep previous targetEl until the next is found so TourOverlay stays mounted for CSS transitions
    const pollForElement = () => {
      const el = safeQuerySelector(step.target);

      if (el) {
        setTargetEl(el);
        return;
      }

      attempts++;

      if (attempts >= MAX_ATTEMPTS) {
        // Element not found after max attempts, skip to next step
        if (index < steps.length - 1) {
          setIndex((i) => i + 1);
        } else {
          // Last step and element not found, complete the tour
          completeRef.current();
        }
        return;
      }

      timeoutId = setTimeout(pollForElement, POLL_INTERVAL);
    };

    // Start polling after a short delay to let React render
    timeoutId = setTimeout(pollForElement, 50);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [open, step?.id, index]);

  // Scroll target element into view when found
  useEffect(() => {
    if (!open || !targetEl) return;
    targetEl.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
  }, [open, targetEl]);

  const next = useCallback(() => {
    if (index >= steps.length - 1) {
      complete();
      return;
    }
    setIndex((i) => i + 1);
  }, [index, steps.length, complete]);

  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  if (!open || !step || !targetEl) return null;

  return (
    <TourOverlay
      step={step}
      index={index}
      total={steps.length}
      targetEl={targetEl}
      spotlightPadding={spotlightPadding}
      allowScroll={allowScroll}
      onNext={next}
      onPrev={prev}
      onClose={close}
      onSkip={complete}
    />
  );
}
